import { PartitionModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { SelectOption } from '../../Common/Components/Selects/Types'
import { PartitionModalSyncLayout } from './partitionModalTelSysLayout'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'

/**
 * Supported partition TelSys component type values returned by the partition API.
 */
export enum PartitionTelSysComponentType {
    UNKNOWN = 'Unknown',
    BROADWORKS_SERVICE_PROVIDER = 'BroadWorks ServiceProvider',
    BROADWORKS_SERVICE_PROVIDER_GROUP = 'BroadWorks ServiceProvider Group',
    BROADWORKS_ENTERPRISE = 'BroadWorks Enterprise',
    BROADWORKS_ENTERPRISE_GROUP = 'BroadWorks Enterprise Group',
    WEBEX_ORGANISATION = 'Webex Organisation',
    WEBEX_LOCATION = 'Webex Location'
}

const BROADWORKS_COMPONENT_TYPE_LANGUAGE_KEYS: Record<PartitionTelSysComponentType, string> = {
    [PartitionTelSysComponentType.UNKNOWN]: PartitionModal.PartitionModal_Sync_ComponentType_Unknown,
    [PartitionTelSysComponentType.BROADWORKS_SERVICE_PROVIDER]:
        PartitionModal.PartitionModal_Sync_ComponentType_BroadWorks_ServiceProvider,
    [PartitionTelSysComponentType.BROADWORKS_SERVICE_PROVIDER_GROUP]:
        PartitionModal.PartitionModal_Sync_ComponentType_BroadWorks_ServiceProviderGroup,
    [PartitionTelSysComponentType.BROADWORKS_ENTERPRISE]:
        PartitionModal.PartitionModal_Sync_ComponentType_BroadWorks_Enterprise,
    [PartitionTelSysComponentType.BROADWORKS_ENTERPRISE_GROUP]:
        PartitionModal.PartitionModal_Sync_ComponentType_BroadWorks_EnterpriseGroup,
    [PartitionTelSysComponentType.WEBEX_ORGANISATION]:
        PartitionModal.PartitionModal_Sync_ComponentType_Webex_Organisation,
    [PartitionTelSysComponentType.WEBEX_LOCATION]: PartitionModal.PartitionModal_Sync_ComponentType_Webex_Location
}

const BROADWORKS_COMPONENT_TYPES: readonly PartitionTelSysComponentType[] = [
    PartitionTelSysComponentType.UNKNOWN,
    PartitionTelSysComponentType.BROADWORKS_ENTERPRISE,
    PartitionTelSysComponentType.BROADWORKS_ENTERPRISE_GROUP,
    PartitionTelSysComponentType.BROADWORKS_SERVICE_PROVIDER,
    PartitionTelSysComponentType.BROADWORKS_SERVICE_PROVIDER_GROUP
]

const WEBEX_COMPONENT_TYPES: readonly PartitionTelSysComponentType[] = [
    PartitionTelSysComponentType.UNKNOWN,
    PartitionTelSysComponentType.WEBEX_ORGANISATION,
    PartitionTelSysComponentType.WEBEX_LOCATION
]

/**
 * Returns the component-type options for the given synchronisation layout.
 *
 * @param language - Active language handler.
 * @param syncLayout - Current partition synchronisation layout.
 * @returns Select options for BroadWorks/Webex, otherwise an empty list.
 */
export const getPartitionTelSysComponentTypeOptions = (
    language: CLanguageHandler,
    syncLayout: PartitionModalSyncLayout
): SelectOption<string>[] => {
    let componentTypes: readonly PartitionTelSysComponentType[] = []

    if (syncLayout === PartitionModalSyncLayout.BROADWORKS) {
        componentTypes = BROADWORKS_COMPONENT_TYPES
    } else if (syncLayout === PartitionModalSyncLayout.WEBEX) {
        componentTypes = WEBEX_COMPONENT_TYPES
    }

    return componentTypes.map(
        (componentType: PartitionTelSysComponentType): SelectOption<string> => ({
            value: componentType,
            label: language.get(BROADWORKS_COMPONENT_TYPE_LANGUAGE_KEYS[componentType])
        })
    )
}

/**
 * Normalises API string values to the select ids used by the React partition modal.
 *
 * @param value - Raw stored component type.
 * @param syncLayout - Current partition synchronisation layout.
 * @returns Normalised component-type id, or an empty string when the layout does not use the select.
 */
export const normalisePartitionTelSysComponentTypeValue = (
    value: Definable<string>,
    syncLayout: PartitionModalSyncLayout
): string => {
    if (syncLayout !== PartitionModalSyncLayout.BROADWORKS && syncLayout !== PartitionModalSyncLayout.WEBEX) return value ?? EMPTY_STRING

    const normalisedInput: string = String(value ?? EMPTY_STRING).trim()

    if (normalisedInput.length === 0) return PartitionTelSysComponentType.UNKNOWN

    const directMatch: Definable<PartitionTelSysComponentType> = Object.values(PartitionTelSysComponentType).find(
        (componentType: PartitionTelSysComponentType): boolean => componentType === normalisedInput
    )

    if (isVarNotEmpty(directMatch)) return directMatch

    return PartitionTelSysComponentType.UNKNOWN
}

/**
 * True when the selected component type requires an owner/provider/enterprise identifier.
 *
 * @param syncLayout - Current partition synchronisation layout.
 * @param componentTypeValue - Selected component type id.
 * @returns Whether the owner id field should be shown.
 */
export const shouldShowPartitionTelSysComponentTypeOwner = (
    syncLayout: PartitionModalSyncLayout,
    componentTypeValue: string
): boolean => {
    if (syncLayout === PartitionModalSyncLayout.WEBEX) return false

    if (syncLayout === PartitionModalSyncLayout.BROADWORKS) {
        return (
            componentTypeValue === PartitionTelSysComponentType.BROADWORKS_ENTERPRISE ||
            componentTypeValue === PartitionTelSysComponentType.BROADWORKS_ENTERPRISE_GROUP ||
            componentTypeValue === PartitionTelSysComponentType.BROADWORKS_SERVICE_PROVIDER ||
            componentTypeValue === PartitionTelSysComponentType.BROADWORKS_SERVICE_PROVIDER_GROUP
        )
    }

    return true
}

/**
 * True when the selected component type requires a group/location/member identifier.
 *
 * @param syncLayout - Current partition synchronisation layout.
 * @param componentTypeValue - Selected component type id.
 * @returns Whether the ID-on-TelSys field should be shown.
 */
export const shouldShowPartitionTelSysComponentTypeId = (
    syncLayout: PartitionModalSyncLayout,
    componentTypeValue: string
): boolean => {
    if (syncLayout === PartitionModalSyncLayout.WEBEX) return componentTypeValue === PartitionTelSysComponentType.WEBEX_LOCATION

    if (syncLayout === PartitionModalSyncLayout.BROADWORKS) {
        return (
            componentTypeValue === PartitionTelSysComponentType.BROADWORKS_ENTERPRISE_GROUP ||
            componentTypeValue === PartitionTelSysComponentType.BROADWORKS_SERVICE_PROVIDER_GROUP
        )
    }

    return true
}
